import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

/** Broadcast Audio Profile — plant format + delivery codec + loudness (LKFS / EBU R.128).
 *  Extracted from ChannelSettingsDialog for maintainability. `ap` is a slice of the
 *  parent state (the channel's `audio_profile`) and `setAp` is its setter. */
export default function AudioProfilePanel({ ap, setAp, patchAp, isAC3, channel }) {
  return (
          <div className="border-t border-zinc-800 pt-4" data-testid="audio-profile-section">
            <div className="flex items-baseline justify-between mb-3">
              <div className="font-display font-bold">Broadcast Audio Profile</div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                Plant · Delivery · Loudness
              </span>
            </div>

            {/* Plant format */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Sample rate</Label>
                <select
                  value={ap.sample_rate_hz}
                  onChange={(e) => patchAp("sample_rate_hz", Number(e.target.value))}
                  className="w-full mt-1 bg-black border border-zinc-800 rounded-sm text-xs px-2 py-2 font-mono"
                  data-testid="ap-sample-rate"
                >
                  <option value={44100}>44.1 kHz</option>
                  <option value={48000}>48 kHz</option>
                  <option value={96000}>96 kHz</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Bit depth</Label>
                <select
                  value={ap.bit_depth}
                  onChange={(e) => patchAp("bit_depth", Number(e.target.value))}
                  className="w-full mt-1 bg-black border border-zinc-800 rounded-sm text-xs px-2 py-2 font-mono"
                  data-testid="ap-bit-depth"
                >
                  <option value={16}>16-bit PCM</option>
                  <option value={20}>20-bit PCM</option>
                  <option value={24}>24-bit PCM</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Primary layout</Label>
                <select
                  value={ap.primary_layout}
                  onChange={(e) => {
                    const layout = e.target.value;
                    const map =
                      layout === "5.1"
                        ? ["L", "R", "C", "LFE", "Ls", "Rs"]
                        : layout === "7.1"
                        ? ["L", "R", "C", "LFE", "Ls", "Rs", "Lb", "Rb"]
                        : ["L", "R"];
                    setAp((p) => ({ ...p, primary_layout: layout, channel_map: map }));
                  }}
                  className="w-full mt-1 bg-black border border-zinc-800 rounded-sm text-xs px-2 py-2 font-mono"
                  data-testid="ap-primary-layout"
                >
                  <option value="2.0">Stereo 2.0</option>
                  <option value="5.1">5.1 Surround</option>
                  <option value="7.1">7.1 Surround</option>
                </select>
              </div>
            </div>

            {/* Delivery encoding */}
            <div className="grid grid-cols-3 gap-3 mt-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Delivery codec</Label>
                <select
                  value={ap.delivery_codec}
                  onChange={(e) => patchAp("delivery_codec", e.target.value)}
                  className="w-full mt-1 bg-black border border-zinc-800 rounded-sm text-xs px-2 py-2 font-mono"
                  data-testid="ap-codec"
                >
                  <option value="AC-3">Dolby Digital (AC-3)</option>
                  <option value="E-AC-3">Dolby Digital Plus (E-AC-3)</option>
                  <option value="AAC-LC">AAC-LC</option>
                  <option value="HE-AAC">HE-AAC v2</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Bitrate (kbps)</Label>
                <select
                  value={ap.delivery_bitrate_kbps}
                  onChange={(e) => patchAp("delivery_bitrate_kbps", Number(e.target.value))}
                  className="w-full mt-1 bg-black border border-zinc-800 rounded-sm text-xs px-2 py-2 font-mono"
                  data-testid="ap-bitrate"
                >
                  {[128, 192, 256, 320, 384, 448, 640, 768].map((b) => (
                    <option key={b} value={b}>{b} kbps</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Downmix mode</Label>
                <select
                  value={ap.downmix_mode}
                  onChange={(e) => patchAp("downmix_mode", e.target.value)}
                  className="w-full mt-1 bg-black border border-zinc-800 rounded-sm text-xs px-2 py-2 font-mono"
                  data-testid="ap-downmix"
                >
                  <option value="LtRt">LtRt (Dolby Surround-compatible)</option>
                  <option value="LoRo">LoRo (matrix-neutral)</option>
                </select>
              </div>
            </div>

            {/* Loudness */}
            <div className="grid grid-cols-3 gap-3 mt-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Loudness spec</Label>
                <select
                  value={ap.loudness_spec}
                  onChange={(e) => {
                    const spec = e.target.value;
                    const tgt = spec === "EBU R.128" ? -23 : spec === "ARIB TR-B32" ? -24 : -24;
                    setAp((p) => ({ ...p, loudness_spec: spec, loudness_target_lkfs: tgt, dialnorm: Math.round(tgt) }));
                  }}
                  className="w-full mt-1 bg-black border border-zinc-800 rounded-sm text-xs px-2 py-2 font-mono"
                  data-testid="ap-loudness-spec"
                >
                  <option value="ATSC A/85">ATSC A/85 (CALM Act)</option>
                  <option value="EBU R.128">EBU R.128</option>
                  <option value="ARIB TR-B32">ARIB TR-B32</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Target ({ap.loudness_spec === "EBU R.128" ? "LUFS" : "LKFS"})
                </Label>
                <Input
                  type="number" step="0.5"
                  value={ap.loudness_target_lkfs}
                  onChange={(e) => patchAp("loudness_target_lkfs", Number(e.target.value))}
                  className="mt-1 bg-black border-zinc-800 font-mono text-xs"
                  data-testid="ap-loudness-target"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Dialnorm {isAC3 ? "(AC-3 metadata)" : "(N/A)"}
                </Label>
                <Input
                  type="number" step="1" min="-31" max="-1"
                  value={ap.dialnorm}
                  disabled={!isAC3}
                  onChange={(e) => patchAp("dialnorm", Number(e.target.value))}
                  className="mt-1 bg-black border-zinc-800 font-mono text-xs disabled:opacity-40"
                  data-testid="ap-dialnorm"
                />
              </div>
            </div>

            {/* Downmix metadata toggle */}
            <div className="mt-3 flex items-center gap-2">
              <input
                id={`downmix-embed-${channel.id}`}
                type="checkbox"
                checked={ap.downmix_embed_metadata}
                onChange={(e) => patchAp("downmix_embed_metadata", e.target.checked)}
                className="accent-red-500"
                data-testid="ap-downmix-embed"
              />
              <label htmlFor={`downmix-embed-${channel.id}`} className="text-xs text-zinc-300">
                Embed stereo downmix metadata in the delivery bitstream
              </label>
            </div>

            {/* Live summary card */}
            <div className="mt-4 border border-zinc-800 rounded-sm bg-black/40 p-3 font-mono text-[11px] leading-relaxed" data-testid="ap-summary">
              <div className="text-zinc-500 uppercase tracking-widest text-[9px] mb-1">Signal chain</div>
              <div className="text-zinc-200">
                <span className="text-emerald-400">Plant</span> · {ap.sample_rate_hz / 1000} kHz / {ap.bit_depth}-bit PCM
                {" → "}
                <span className="text-emerald-400">Mix</span> · {ap.primary_layout}
                {" ("}{(ap.channel_map || []).join(" ")}{")"}
                {" → "}
                <span className="text-emerald-400">Encode</span> · {ap.delivery_codec} @ {ap.delivery_bitrate_kbps} kbps
                {" → "}
                <span className="text-emerald-400">Downmix</span> · {ap.downmix_mode}{" "}
                {ap.downmix_embed_metadata ? "(embedded)" : "(separate)"}
                {" → "}
                <span className="text-emerald-400">Loudness</span> · {ap.loudness_target_lkfs} LKFS {isAC3 && `(dialnorm ${ap.dialnorm})`}
              </div>
              <div className="text-zinc-500 mt-2">
                Compliance: <span className="text-amber-300">{ap.loudness_spec}</span>
                {" · True-peak ceiling "}
                {ap.true_peak_ceiling_dbtp} dBTP
              </div>
            </div>
          </div>

  );
}
